import React, { RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
// @ts-ignore
import { ScrollSyncPane } from 'react-scroll-sync';
import { classes, st } from './FloatingScrollBar.st.css';
import { useFloatingScrollBarContext } from './FloatingScrollBarContext';
import { useFloatingScrollBarState } from './useFloatingScrollBarState';
import { usePageScrollContext } from '../../Page/PageScrollContext';

export interface FloatingScrollBarBaseProps {
  zIndex?: number;
}

export interface FloatingScrollBarProps extends FloatingScrollBarBaseProps {
  dataHook?: string;
}

interface FloatingScrollBarInnerProps
  extends FloatingScrollBarProps, ReturnType<typeof useFloatingScrollBarState> {
  viewportElement: HTMLElement;
  scrollBarRef: RefObject<HTMLDivElement>;
}

function FloatingScrollBarInner(props: FloatingScrollBarInnerProps) {
  const {
    dataHook,
    viewportElement,
    scrollBarRef,
    zIndex,
    scrollContentSize,
    floatingPosition,
    scrollContentPosition,
  } = props;

  const [height, setHeight] = useState(0);

  useEffect(() => {
    const scrollBarElement = scrollBarRef.current;
    if (!scrollBarElement) {
      return;
    }
    setHeight(scrollBarElement.getBoundingClientRect().height);
  }, [scrollBarRef]);

  return createPortal(
    <div
      data-hook={dataHook}
      className={st(classes.root)}
      ref={scrollBarRef}
      style={{
        ...scrollContentPosition,
        zIndex,
        ...(floatingPosition
          ? { top: floatingPosition.top - height }
          : { visibility: 'hidden' }),
      }}
    >
      <div
        data-hook="floating-scroll-bar-content"
        className={st(classes.content)}
        style={scrollContentSize}
      />
    </div>,
    viewportElement,
  );
}

export function FloatingScrollBar(props: FloatingScrollBarProps) {
  const { viewportRef } = useFloatingScrollBarContext();
  const { scrollableContentRef: pageScrollableContentRef } =
    usePageScrollContext();
  const [viewportElement, setViewportElement] = useState<HTMLElement | null>(
    null,
  );
  const scrollBarRef = useRef<HTMLDivElement>(null);
  const { floatingScrollBarStateRef } = useFloatingScrollBarContext();

  const { scrollContentPosition, scrollContentSize, floatingPosition, state } =
    useFloatingScrollBarState();

  useEffect(() => {
    floatingScrollBarStateRef.current = state;
    return () => {
      floatingScrollBarStateRef.current = null;
    };
  }, [floatingScrollBarStateRef, state]);

  useEffect(() => {
    const pageElement = pageScrollableContentRef?.current?.parentElement;

    if (pageElement) {
      setViewportElement(pageElement);
    } else {
      setViewportElement(viewportRef?.current ?? null);
    }
  }, [viewportRef, pageScrollableContentRef]);

  if (!viewportElement) {
    return null;
  }

  return (
    <ScrollSyncPane attachTo={scrollBarRef as any}>
      <FloatingScrollBarInner
        {...props}
        viewportElement={viewportElement}
        scrollBarRef={scrollBarRef}
        scrollContentPosition={scrollContentPosition}
        scrollContentSize={scrollContentSize}
        floatingPosition={floatingPosition}
        state={state}
      />
    </ScrollSyncPane>
  );
}
